import { Menu, MoreVert } from '@mui/icons-material';
import { AppBar, Box, Button, Grid, IconButton, Link, MenuItem, Select, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import { getCurrentPage } from '../modules/api/client';
import LoginModal from '../nav/LoginModal';
import MobileBottomNav from '../nav/MobileBottomNav';
import SignupModal from '../nav/SignupModal';
import { Language2EndpointPostPromise } from '../routes/Language';
import './nav.css';
import { theme } from './theme';
import useTranslationState from './useTranslationState';

function Nav() {
    const navigate = useNavigate();

    const languageState = useTranslationState({
        ukoni: '',
        home: '',
        prices: '',
        config: '',
        about: '',
        login: '',
        trynow: '',
        overview: '',
        signup: '',
        create: '',
        changeLanguage: '',
        userLanguageSetting: '',
        privateChefs: '',
        rentalChefs: '',
    });
    const isMobile = useIsMobile();

    const [language, setLanguage] = useState('');

    useEffect(() => {
        if (languageState.userLanguageSetting !== '') {
            setLanguage(languageState.userLanguageSetting);
        }
    }, [languageState]);

    useEffect(() => {
        if (language === '' || language === languageState.userLanguageSetting) {
            return;
        }

        const promise = Language2EndpointPostPromise(language);

        promise.then(() => {
            navigate(0);
        });
    }, [language]);

    const [isLogin, setIsLogin] = useState(false);

    const [menuOpen, setMenuOpen] = useState(false);

    const openMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const navigateToHomeAndScrollTo = (id: string) => {
        navigate('/');
        setTimeout(() => {
            const element = document.getElementById(id) as HTMLElement;
            if (element) {
                window.scrollTo({
                    top: element.offsetTop - 90,
                    behavior: 'smooth',
                });
            }
        }, 500);
        openMenu();
    };

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    height: 80,
                    alignItems: 'center',
                    paddingX: { xs: 4, md: 10 },
                    backgroundColor: theme.palette.primary.dark + 'BB',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
                    boxShadow: 0,
                    backdropFilter: 'blur(15px)',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 4,
                    }}
                >
                    <IconButton
                        onClick={openMenu}
                    >
                        <Menu
                            sx={{
                                color: 'rgba(205, 205, 205,1)',
                            }}
                        />
                    </IconButton>
                    <img
                        src="logo.png"
                        alt="Ukoni"
                        style={{
                            height: 120,
                            width: 'auto',
                            marginTop: -20,
                            borderRadius: 25,
                            filter: 'invert(1)',
                        }}
                    />
                    <Box sx={{ width: 23 }}>
                    </Box>
                </Box>
            </AppBar>
            <Box
                sx={{
                    position: 'fixed',
                    top: 80,
                    left: 0,
                    width: '100%',
                    height: 'auto',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'center',
                    textAlign: 'center',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
                    py: 4,
                    flexDirection: 'column',
                    // Dont register clicks if menu is closed
                    pointerEvents: menuOpen ? 'auto' : 'none',
                    cursor: menuOpen ? 'pointer' : 'default',
                    // Background transparent towards the bottom
                    backgroundColor: theme.palette.primary.dark + 'AA',
                    backdropFilter: 'blur(15px)',
                    // Transition into existence
                    opacity: menuOpen ? 1 : 0,
                    // Fade in and out
                    animation: menuOpen ? 'fadeIn 0.3s ease-in-out' : 'fadeOut 0.2s ease-in-out', // Control animation
                    // also fade out
                    animationFillMode: 'forwards',
                    '@keyframes fadeIn': {
                        '0%': {
                            opacity: 0,
                        },
                        '100%': {
                            opacity: 1,
                        },
                    },
                    '@keyframes fadeOut': {
                        '0%': {
                            opacity: 1,
                        },
                        '100%': {
                            opacity: 0,
                        },
                    },
                }}
            >
                <Link
                    onClick={() => {
                        navigate('/');
                        openMenu();
                    }}
                    sx={{
                        color: 'white',
                        fontSize: 20,
                        textDecoration: 'none',
                        userSelect: 'none',
                        cursor: 'pointer',
                    }}
                >
                    {languageState.home}
                </Link>
                <Link
                    onClick={() => {
                        navigateToHomeAndScrollTo('privatechefs');
                    }}
                    sx={{
                        color: 'white',
                        fontSize: 20,
                        textDecoration: 'none',
                        mt: 2,
                        userSelect: 'none',
                        cursor: 'pointer',
                    }}
                >
                    {languageState.privateChefs}
                </Link>
                <Link
                    onClick={() => {
                        navigateToHomeAndScrollTo('rentalchefs');
                    }}
                    sx={{
                        color: 'white',
                        fontSize: 20,
                        textDecoration: 'none',
                        mt: 2,
                        userSelect: 'none',
                        cursor: 'pointer',
                    }}
                >
                    {languageState.rentalChefs}
                </Link>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: 4,
                    }}
                >
                    <Box
                        sx={{
                            mt: 2,
                            width: 400,
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            padding: 1,
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                marginBottom: 2,
                            }}
                        >
                            {languageState.changeLanguage}
                        </Typography>
                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid item xs={6} md={4}>
                                <Link
                                    href=""
                                    onClick={() => setLanguage('english')}
                                    sx={{
                                        color: 'white',
                                        fontSize: 15,
                                        textDecoration: 'none',
                                    }}
                                >
                                    English 🇺🇸
                                </Link>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Link
                                    href=""
                                    onClick={() => setLanguage('german')}
                                    sx={{
                                        color: 'white',
                                        fontSize: 15,
                                        textDecoration: 'none',
                                    }}
                                >
                                    Deutsch 🇩🇪
                                </Link>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Link
                                    href=""
                                    onClick={() => setLanguage('french')}
                                    sx={{
                                        color: 'white',
                                        fontSize: 15,
                                        textDecoration: 'none',
                                    }}
                                >
                                    Français 🇫🇷
                                </Link>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Link
                                    href=""
                                    onClick={() => setLanguage('spanish')}
                                    sx={{
                                        color: 'white',
                                        fontSize: 15,
                                        textDecoration: 'none',
                                    }}
                                >
                                    Español 🇪🇸
                                </Link>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Link
                                    href=""
                                    onClick={() => setLanguage('italian')}
                                    sx={{
                                        color: 'white',
                                        fontSize: 15,
                                        textDecoration: 'none',
                                    }}
                                >
                                    Italiano 🇮🇹
                                </Link>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Link
                                    href=""
                                    onClick={() => setLanguage('dutch')}
                                    sx={{
                                        color: 'white',
                                        fontSize: 15,
                                        textDecoration: 'none',
                                    }}
                                >
                                    Nederlands 🇳🇱
                                </Link>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Link
                                    href=""
                                    onClick={() => setLanguage('portuguese')}
                                    sx={{
                                        color: 'white',
                                        fontSize: 15,
                                        textDecoration: 'none',
                                    }}
                                >
                                    Português 🇵🇹
                                </Link>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    height: isMobile ? 80 : 80,
                    width: '100%',
                    backgroundColor: theme.palette.primary.dark,
                }}
            >
            </Box>
        </>
    );
}

export default Nav;
